import { Field, Flex, HStack, RadioGroup, Text } from '@chakra-ui/react'
import { AmountInput } from 'commons/components/amount/AmountInput'
import { AmountRadioButton } from 'commons/components/amount/AmountRadioButton'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { RoundButton } from 'commons/components/layout/RoundButton'
import { Section } from 'commons/components/layout/Section'
import { useFieldContext } from 'commons/config/form'
import { LYDIA_PAGE_AMOUNTS, MONEY_POT_AMOUNTS } from 'commons/constants/amount'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { sanitizeRaw } from 'commons/utils/amountHelpers'
import type { ChangeEvent } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const AmountSection = ({ isFromMoneyPot = false }: { isFromMoneyPot?: boolean }) => {
	const field = useFieldContext<string>()
	const predefinedAmounts = isFromMoneyPot ? MONEY_POT_AMOUNTS : LYDIA_PAGE_AMOUNTS
	const initialIsPredefined = predefinedAmounts.includes(field.state.value)
	const [isPredefinedAmount, setIsPredefinedAmount] = useState(initialIsPredefined)
	const inputRef = useRef<HTMLInputElement>(null)
	const { t } = useTranslation()

	const handleClick = useCallback(() => {
		setIsPredefinedAmount(false)
		field.handleChange('')
		setTimeout(() => {
			inputRef.current?.focus()
		}, 0)
	}, [field])

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => field.handleChange(sanitizeRaw(String(e.target.value))),
		[field]
	)

	return (
		<Field.Root invalid={!field.state.meta.isValid}>
			<Section
				borderTopRadius='xl'
				position='relative'
				_focusWithin={{
					outline: !isPredefinedAmount ? `2px solid ${BgColors.OPPOSITE_Z3}` : undefined
				}}
				_groupInvalid={{
					outline: `2px solid ${TextColors.STATUS_ERROR}`
				}}
			>
				{isFromMoneyPot && (
					<Text fontFamily='Degular' fontWeight={600} fontSize='20px' mb='30px'>
						{t('phone_form.participate')}
					</Text>
				)}
				{isPredefinedAmount ? (
					<RadioGroup.Root
						name={field.name}
						value={field.state.value}
						onValueChange={({ value }: { value: string | null }) => field.handleChange(value ?? '')}
					>
						<HStack gap={3}>
							{(isFromMoneyPot ? MONEY_POT_AMOUNTS : LYDIA_PAGE_AMOUNTS).map((value) => (
								<AmountRadioButton key={value} value={String(value)}>
									{t('phone_form.amount_format', { value })}
								</AmountRadioButton>
							))}
							<RoundButton onClick={handleClick}>{t('phone_form.other_amount')}</RoundButton>
						</HStack>
					</RadioGroup.Root>
				) : (
					<Flex direction='column'>
						<AmountInput ref={inputRef} onChange={handleChange} value={field.state.value} />
					</Flex>
				)}
				<FormErrors errors={field.state.meta.errors} />
			</Section>
		</Field.Root>
	)
}
